import { Box, Card, CardContent, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { FilterAlt, ArrowForward } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useGetStudentsQuery } from "../../services/students.service";
import { useAppTheme } from "../../context/ThemeContext";

const STAGES = [
  {
    label: "Intake",
    statuses: ["initial"],
    color: "#64748B",
  },
  {
    label: "Visa",
    statuses: ["visa", "visa:reminder", "visa:preparing", "visa:submitted"],
    color: "#0EA5E9",
  },
  {
    label: "Admission",
    statuses: ["admission:reminder", "admission:preparing", "admission:submitted"],
    color: "#8B5CF6",
  },
  {
    label: "Registration",
    statuses: ["registration", "registration:reminder", "deferment:preparing", "deferment:submitted"],
    color: "#F59E0B",
  },
  {
    label: "Pickup",
    statuses: ["pickup:reminder"],
    color: "#EC4899",
  },
  {
    label: "Commission",
    statuses: ["commission:pending"],
    color: "#F97316",
  },
  {
    label: "Completed",
    statuses: ["completed successfully", "cooperation done"],
    color: "#059669",
  },
];

function ConversionFunnelWidget() {
  const { data: students } = useGetStudentsQuery("Students");
  const { isNewTheme, accentColor } = useAppTheme();

  const total = students?.length ?? 0;

  const stageCounts = STAGES.map((stage) => ({
    ...stage,
    count: (students ?? []).filter((s) => stage.statuses.includes(s.status)).length,
  }));

  const maxCount = Math.max(...stageCounts.map((s) => s.count), 1);

  const cardSx = isNewTheme
    ? {
        width: "100%",
        borderRadius: "8px",
        border: "1px solid #E2E8F0",
        boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
      }
    : { width: "100%", borderRadius: "10px", boxShadow: "0px 0px 55px 5px rgba(0,0,0,0.05)" };

  return (
    <Card sx={cardSx}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isNewTheme && (
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: `${accentColor}1a`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FilterAlt sx={{ fontSize: 18, color: accentColor }} />
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
              Conversion Funnel
            </Typography>
            {isNewTheme && <Box sx={{ width: 32, height: 3, borderRadius: 999, bgcolor: accentColor, mt: 0.5 }} />}
          </Box>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="caption" color="text.secondary">{total} total</Typography>
          <Link to="/students" style={{ textDecoration: "none" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, color: "primary.main", fontSize: "0.75rem", fontWeight: 600, opacity: 0.85, "&:hover": { opacity: 1 } }}>
              View all <ArrowForward sx={{ fontSize: 13 }} />
            </Box>
          </Link>
        </Stack>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ px: 2.5, py: 2 }}>
        {total === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", py: 2, fontStyle: "italic" }}>
            No students yet
          </Typography>
        ) : (
          <Stack spacing={1}>
            {stageCounts.map((stage, i) => {
              const pct = total > 0 ? Math.round((stage.count / total) * 100) : 0;
              const barWidth = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;

              return (
                <Tooltip
                  key={stage.label}
                  title={`${stage.count} students (${pct}% of total) · ${stage.statuses.join(", ")}`}
                  arrow
                >
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.4}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: stage.color, flexShrink: 0 }} />
                        <Typography variant="caption" fontWeight={600} color="text.primary" sx={{ fontSize: "0.72rem" }}>
                          {stage.label}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: stage.color, fontSize: "0.72rem" }}>
                          {stage.count}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem", minWidth: 28, textAlign: "right" }}>
                          {pct}%
                        </Typography>
                      </Stack>
                    </Stack>

                    <Box sx={{ height: 8, borderRadius: 999, bgcolor: "#F1F5F9", overflow: "hidden" }}>
                      <Box
                        sx={{
                          height: "100%",
                          width: `${barWidth}%`,
                          bgcolor: stage.color,
                          borderRadius: 999,
                          transition: "width 0.5s ease",
                          opacity: stage.count === 0 ? 0.25 : 1,
                        }}
                      />
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}

            {/* Completion rate summary */}
            {(() => {
              const completed = stageCounts.find((s) => s.label === "Completed")?.count ?? 0;
              const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
              return (
                <Box sx={{ mt: 1, pt: 1.5, borderTop: "1px solid #F1F5F9" }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Overall completion rate
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color: "#059669" }}>
                      {rate}%
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 0.5, height: 6, borderRadius: 999, bgcolor: "#F1F5F9", overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${rate}%`, bgcolor: "#059669", borderRadius: 999, transition: "width 0.5s ease" }} />
                  </Box>
                </Box>
              );
            })()}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default ConversionFunnelWidget;
